// client/src/components/pages/CustomSummaries/ExecuteSummaryModal.js
import React, { useState, useEffect, useRef } from 'react';
import {
  FaTimes,
  FaPlay,
  FaDownload,
  FaTable,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaClock,
  FaDatabase,
  FaExclamationTriangle,
  FaCheckCircle,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa';
import api from '../../../services/api';
import LexiBridgeLoader from '../../common/LexiBridgeLoader';
import LoadingSpinner from '../../common/LoadingSpinner';
import DataVisualization from './DataVisualization';
import ThemedSelect from '../../common/ThemedSelect';
import { toApiDateString } from '../../../utils/dateUtils';

const ExecuteSummaryModal = ({ summary, onClose, onExecuted }) => {
  const [parameters, setParameters] = useState({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const hasParams =
    summary && summary.QueryParameters && Object.keys(summary.QueryParameters).length > 0;
  const [activeTab, setActiveTab] = useState(hasParams ? 'parameters' : 'results');
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const hasAutoExecuted = useRef(false);

  // Initialize parameters from summary
  useEffect(() => {
    if (summary && summary.QueryParameters) {
      const initialParams = {};
      Object.entries(summary.QueryParameters).forEach(([key, config]) => {
        if (config.default !== undefined) {
          initialParams[key] = config.default;
        } else {
          // Set default values based on type
          switch (config.type) {
            case 'date':
              initialParams[key] = toApiDateString(new Date());
              break;
            case 'number':
              initialParams[key] = 0;
              break;
            case 'boolean':
              initialParams[key] = false;
              break;
            default:
              initialParams[key] = '';
          }
        }
      });
      setParameters(initialParams);
    }
  }, [summary]);

  // Auto-execute when modal opens for summaries without parameters
  useEffect(() => {
    if (!hasParams && summary && !hasAutoExecuted.current) {
      hasAutoExecuted.current = true;
      handleExecute();
    }
  }, [summary, hasParams]);

  const handleParameterChange = (paramName, value) => {
    setParameters((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setError(null);
      setResult(null);

      const response = await api.customSummaries.execute(summary.SummaryID, parameters);

      setResult(response.data);
      setActiveTab('results');

      if (onExecuted) {
        onExecuted();
      }
    } catch (err) {
      console.error('Error executing summary:', err);
      setError(
        err.response?.data?.error || err.response?.data?.message || 'Failed to execute summary'
      );
    } finally {
      setExecuting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      setExportFormat(format);

      let response;
      switch (format) {
        case 'csv':
          response = await api.customSummaries.export.csv(summary.SummaryID, parameters);
          break;
        case 'excel':
          response = await api.customSummaries.export.excel(summary.SummaryID, parameters);
          break;
        case 'pdf':
          response = await api.customSummaries.export.pdf(summary.SummaryID, parameters);
          break;
        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      // Download using authenticated fetch via the api instance
      if (response.data.downloadUrl) {
        const downloadResponse = await api.get(response.data.downloadUrl.replace('/api', ''), {
          responseType: 'blob',
        });

        // Create blob URL and trigger download
        const blob = new Blob([downloadResponse.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `export.${format === 'excel' ? 'xlsx' : format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(`Error exporting to ${format}:`, err);
      setError(
        err.response?.data?.error || err.response?.data?.message || `Failed to export to ${format}`
      );
    } finally {
      setExporting(false);
      setExportFormat(null);
    }
  };

  // Client-side CSV fallback using already-fetched data
  const handleClientExport = (format) => {
    if (!result || !result.data || result.data.length === 0) return;

    if (format === 'csv') {
      const headers = Object.keys(result.data[0]);
      const csvRows = [
        headers.join(','),
        ...result.data.map((row) =>
          headers
            .map((h) => {
              const val = row[h];
              if (val === null || val === undefined) return '';
              const str = String(val);
              // Escape quotes and wrap in quotes if contains comma/quote/newline
              if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(',')
        ),
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${summary.Title || 'export'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const renderParameterInput = (paramName, config) => {
    const value = parameters[paramName] || '';

    switch (config.type) {
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required={config.required}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleParameterChange(paramName, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required={config.required}
          />
        );

      case 'boolean':
        return (
          <ThemedSelect
            value={value.toString()}
            onChange={(e) => handleParameterChange(paramName, e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required={config.required}
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </ThemedSelect>
        );

      case 'select':
        return (
          <ThemedSelect
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required={config.required}
          >
            <option value="">Select an option</option>
            {config.options &&
              config.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </ThemedSelect>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={config.placeholder || `Enter ${paramName}`}
            required={config.required}
          />
        );
    }
  };

  const getVisualizationIcon = (type) => {
    switch (type) {
      case 'chart':
        return <FaChartBar className="text-blue-500" />;
      case 'line':
        return <FaChartLine className="text-green-500" />;
      case 'pie':
        return <FaChartPie className="text-purple-500" />;
      default:
        return <FaTable className="text-gray-500" />;
    }
  };

  const hasParameters =
    summary && summary.QueryParameters && Object.keys(summary.QueryParameters).length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getVisualizationIcon(summary.VisualizationType)}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{summary.Title}</h2>
              <p className="text-gray-600">{summary.Description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {hasParameters && (
              <button
                onClick={() => setActiveTab('parameters')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'parameters'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Parameters
              </button>
            )}
            <button
              onClick={() => setActiveTab('results')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'results'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Results
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Parameters Tab */}
          {activeTab === 'parameters' && hasParameters && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(summary.QueryParameters).map(([paramName, config]) => (
                  <div key={paramName}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {config.label || paramName}
                      {config.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderParameterInput(paramName, config)}
                    {config.description && (
                      <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center pt-6">
                <button
                  onClick={handleExecute}
                  disabled={executing}
                  className="flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors duration-200"
                >
                  {executing ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <FaPlay className="mr-2" />
                      Execute Summary
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div className="space-y-6">
              {executing && (
                <div className="flex items-center justify-center py-16">
                  <LexiBridgeLoader message="Executing summary" />
                </div>
              )}

              {!result && !error && !executing && (
                <div className="text-center py-12">
                  <FaPlay className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Execute</h3>
                  <p className="text-gray-600 mb-4">
                    {hasParameters
                      ? 'Configure parameters and click execute to run this summary.'
                      : 'Click execute to run this summary.'}
                  </p>
                  <button
                    onClick={handleExecute}
                    disabled={executing}
                    className="flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors duration-200 mx-auto"
                  >
                    <FaPlay className="mr-2" />
                    Execute Summary
                  </button>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center">
                    <FaExclamationTriangle className="text-red-500 mr-3" />
                    <div>
                      <h3 className="text-lg font-medium text-red-800">Execution Error</h3>
                      <p className="text-red-600 mt-1">{error}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setError(null);
                      handleExecute();
                    }}
                    className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {result && (
                <div className="space-y-6">
                  {/* Result Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-3" />
                      <div>
                        <h3 className="text-lg font-medium text-green-800">Execution Successful</h3>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-green-600">
                          <div className="flex items-center">
                            <FaDatabase className="mr-1" />
                            <span>{result.rowCount} rows</span>
                          </div>
                          <div className="flex items-center">
                            <FaClock className="mr-1" />
                            <span>{result.executionTime}ms</span>
                          </div>
                          {result.cached && (
                            <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs">
                              Cached
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Export Options */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Results</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                        className="flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm transition-colors duration-200"
                      >
                        {exporting && exportFormat === 'csv' ? (
                          <LoadingSpinner size="sm" color="white" className="mr-1" />
                        ) : (
                          <FaDownload className="mr-1" />
                        )}
                        CSV
                      </button>
                      <button
                        onClick={() => handleExport('excel')}
                        disabled={exporting}
                        className="flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg text-sm transition-colors duration-200"
                      >
                        {exporting && exportFormat === 'excel' ? (
                          <LoadingSpinner size="sm" color="white" className="mr-1" />
                        ) : (
                          <FaDownload className="mr-1" />
                        )}
                        Excel
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        disabled={exporting || result.rowCount > 1000}
                        className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg text-sm transition-colors duration-200"
                        title={result.rowCount > 1000 ? 'PDF export limited to 1000 rows' : ''}
                      >
                        {exporting && exportFormat === 'pdf' ? (
                          <LoadingSpinner size="sm" color="white" className="mr-1" />
                        ) : (
                          <FaDownload className="mr-1" />
                        )}
                        PDF
                      </button>
                    </div>
                  </div>

                  {/* Data Visualization */}
                  <DataVisualization
                    data={result.data}
                    visualizationType={result.summary.visualizationType}
                    chartConfig={result.summary.chartConfig}
                    title={result.summary.title}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {result && `${result.rowCount} rows returned in ${result.executionTime}ms`}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              Close
            </button>
            {result && (
              <button
                onClick={handleExecute}
                disabled={executing}
                className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors duration-200"
              >
                {executing ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Re-executing...
                  </>
                ) : (
                  <>
                    <FaPlay className="mr-2" />
                    Re-execute
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecuteSummaryModal;
